import type { AnyFormApi } from '@tanstack/react-form';
import type { DefaultError, MutationOptions } from '@tanstack/react-query';
import type React from 'react';
import type { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { isApiError } from '@_/utils-frontend';

export class MutationShortCircuitError extends Error {}

export function useFormMutation<
  Schema extends z.ZodType,
  RQData = unknown,
  RQError = DefaultError,
  RQContext = unknown
>({
  form,
  formSchema,
  mutationOptions
}: {
  form: AnyFormApi;
  formSchema: Schema;
  mutationOptions: MutationOptions<RQData, RQError, z.output<Schema>, RQContext>;
}) {
  const mutation = useMutation({
    ...mutationOptions,
    mutationFn: (data, context) => {
      return mutationOptions.mutationFn!(formSchema.parse(data), context);
    }
  });

  const submit = useCallback(
    async (event?: React.SubmitEvent<HTMLFormElement>) => {
      event?.preventDefault();
      event?.stopPropagation();

      try {
        await form.handleSubmit({
          mutateAsync: (value: z.output<Schema>) => mutation.mutateAsync(value)
        });
      } catch (error) {
        if (!isApiError(error)) {
          throw error;
        }
      }
    },
    [form, mutation]
  );

  return {
    mutation,
    submit
  };
}

export function createFormMutationSubmitHandler() {
  return async ({ meta, value }: { meta: unknown; value: unknown }) => {
    if (
      !(
        meta &&
        typeof meta === 'object' &&
        'mutateAsync' in meta &&
        typeof meta.mutateAsync === 'function'
      )
    ) {
      throw new Error(
        'expected form to be submitted using the submit handler from useFormMutation()'
      );
    }

    await meta.mutateAsync(value);
  };
}
